import * as React from "react";
import {
    Toaster as Sonner,
    type ToastClassnames,
    type ToasterProps
} from "sonner";

import { cn } from "../../utils/cn";

const BRANDED_TOAST_CLASSNAMES: ToastClassnames = {
    toast: cn(
        "group toast font-sans rounded-none border border-line-base bg-surface-raised text-foreground-base shadow-none",
        "!rounded-none !shadow-none",
        "p-4 min-h-0 w-[calc(100vw-2rem)] max-w-[440px]"
    ),
    content: "gap-1 pr-8",
    title: "font-sans text-sm leading-5 md:text-base md:leading-6 font-normal text-foreground-base",
    description: "font-sans text-sm leading-5 text-foreground-neutral",
    closeButton: cn(
        "!left-auto !right-2 !top-2 !translate-x-0 !translate-y-0",
        "!rounded-none !shadow-none",
        "border border-line-base bg-surface-sunken text-foreground-muted",
        "transition-colors duration-150 ease-[var(--ease-out-quad)]",
        "hover:border-foreground-base hover:text-foreground-base"
    ),
    actionButton: cn(
        "!rounded-none !shadow-none",
        "bg-surface-inverse-raised px-3 py-1 font-sans text-sm leading-5 text-foreground-inverse",
        "transition-colors duration-150 ease-[var(--ease-out-quad)] hover:bg-surface-inverse-sunken"
    ),
    cancelButton: cn(
        "!rounded-none !shadow-none",
        "border border-line-base bg-surface-sunken px-3 py-1 font-sans text-sm leading-5 text-foreground-base",
        "transition-colors duration-150 ease-[var(--ease-out-quad)] hover:border-foreground-base"
    ),
    error: "border-error-base",
    warning: "border-warning-base",
    success: "border-success-base",
    info: "border-info-base"
};

function mergeClassNames(
    overrides: ToastClassnames | undefined
): ToastClassnames {
    if (!overrides) return BRANDED_TOAST_CLASSNAMES;

    return {
        ...BRANDED_TOAST_CLASSNAMES,
        ...overrides,
        toast: cn(BRANDED_TOAST_CLASSNAMES.toast, overrides.toast),
        content: cn(BRANDED_TOAST_CLASSNAMES.content, overrides.content),
        title: cn(BRANDED_TOAST_CLASSNAMES.title, overrides.title),
        description: cn(
            BRANDED_TOAST_CLASSNAMES.description,
            overrides.description
        ),
        closeButton: cn(
            BRANDED_TOAST_CLASSNAMES.closeButton,
            overrides.closeButton
        ),
        actionButton: cn(
            BRANDED_TOAST_CLASSNAMES.actionButton,
            overrides.actionButton
        ),
        cancelButton: cn(
            BRANDED_TOAST_CLASSNAMES.cancelButton,
            overrides.cancelButton
        )
    };
}

function Toaster({
    toastOptions,
    position = "top-right",
    offset = { top: 96, right: 24 },
    mobileOffset = { top: 80, left: 16, right: 16 },
    gap = 8,
    closeButton = true,
    ...props
}: ToasterProps): React.ReactElement {
    return (
        <Sonner
            data-slot="sonner"
            className="toaster group"
            position={position}
            offset={offset}
            mobileOffset={mobileOffset}
            gap={gap}
            closeButton={closeButton}
            toastOptions={{
                duration: 6000,
                ...toastOptions,
                classNames: mergeClassNames(toastOptions?.classNames)
            }}
            {...props}
        />
    );
}

export { Toaster };
export { toast } from "sonner";
